import React, { useState } from 'react';
import { PU_GOLD_GRAD } from '../../patient-urgency/config';
import type { PuPatient, PuPractice } from '../../patient-urgency/types';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import {
  puBtnPrimary,
  puCard,
  puContainer,
  puDisplay,
  puInput,
  puRise,
  puRoot,
} from './puClasses';

export interface PatientUrgencyDOBGateProps {
  practice: PuPractice;
  patient: PuPatient;
  onVerified: () => void;
  verifyDob?: (dob: string) => Promise<boolean>;
  description?: string;
  submitLabel?: string;
}

export function PatientUrgencyDOBGate({
  practice,
  patient,
  onVerified,
  verifyDob,
  description = 'Please verify your date of birth to view your treatment information.',
  submitLabel = 'View my treatment',
}: PatientUrgencyDOBGateProps) {
  const [dob, setDob] = useState('');
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!dob) return;
    setSubmitting(true);
    setError(false);

    try {
      const verified = verifyDob
        ? await verifyDob(dob)
        : dob === patient.dobMatch;

      if (verified) {
        onVerified();
        return;
      }

      setError(true);
      setSubmitting(false);
    } catch {
      setError(true);
      setSubmitting(false);
    }
  };

  return (
    <div className={cn(puRoot, 'flex items-center justify-center px-[18px] py-6')}>
      <div className={cn(puContainer, 'max-w-[440px]')}>
        <div className={cn(puRise(0), 'mb-[30px] text-center')}>
          <div
            className={cn(
              puDisplay,
              'mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-[15px] text-[22px] font-semibold tracking-[-0.02em] text-white shadow-[var(--sh-2)]',
            )}
            style={{ background: PU_GOLD_GRAD }}
          >
            {practice.logoMark}
          </div>
          <h1 className={cn(puDisplay, 'm-0 mb-1.5 text-[27px] font-semibold text-[var(--ink-0)]')}>
            {practice.name}
          </h1>
          <div className="text-[13px] text-[var(--ink-3)]">{practice.tagline}</div>
        </div>

        <div className={cn(puCard, puRise(1), 'px-6 py-7')}>
          <h2
            className={cn(
              puDisplay,
              'm-0 mb-2 text-[23px] font-semibold leading-[1.2] tracking-[-0.025em] text-[var(--ink-0)]',
            )}
          >
            Welcome back.
          </h2>
          <p className="m-0 mb-[22px] text-[15px] leading-[1.55] text-[var(--ink-2)]">
            {description}
          </p>

          <label className="mb-2 block font-[var(--font-mono)] text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
            Date of birth
          </label>
          <input
            type="date"
            value={dob}
            onChange={(e) => {
              setDob(e.target.value);
              setError(false);
            }}
            className={puInput}
            style={{ marginBottom: error ? 8 : 18 }}
            onKeyDown={(e) => e.key === 'Enter' && void submit()}
          />

          {error ? (
            <div className="mb-3.5 flex items-center gap-2 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--terracotta)_24%,transparent)] bg-[var(--terracotta-tint)] px-3.5 py-2.5 text-[13.5px] text-[var(--terracotta)]">
              <Icon kind="alert" size={15} />
              <span>That doesn’t match. Please check and try again.</span>
            </div>
          ) : null}

          <button type="button" onClick={() => void submit()} disabled={!dob || submitting} className={puBtnPrimary}>
            {submitting ? 'Verifying…' : submitLabel}
            <Icon kind="arrow-r" size={15} />
          </button>

          <div className="mt-[22px] flex items-start gap-2.5 border-t border-[var(--cream-2)] pt-4 text-xs leading-[1.55] text-[var(--ink-3)]">
            <Icon kind="lock" size={13} className="mt-0.5 shrink-0 text-[var(--ink-4)]" />
            <span>
              Your dental and insurance information is private. We use your date of birth to confirm
              it’s you.
            </span>
          </div>
        </div>

        <div className={cn(puRise(2), 'mt-5 text-center text-xs text-[var(--ink-3)]')}>
          Trouble accessing? Call us at{' '}
          <a
            href={`tel:${practice.phone}`}
            className="font-semibold text-[var(--success)] no-underline"
          >
            {practice.phone}
          </a>
        </div>
      </div>
    </div>
  );
}
